import { MAX_PAGES, openCamera, fileToCanvas, finishCanvas, recolorBlob } from "./capture";
import { detectorUnavailable, detectPageCorners, lastDetectDiag, warmDetector, type NormQuad } from "./detect";
import { type ColorMode } from "./scanlib";
import { qrSupported, scanQR, tokenFromPayload } from "./scanner";
import { redeemPairing, processScan, getSignStatus } from "./api";
import { displayContext, probeSession } from "./session";
import { setRegion } from "./locale";
import { loadSnap, saveSnap, clearSnaps } from "./cache";
import type { ScanState } from "./states";

// ───────────────────────── helpers ─────────────────────────
const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);
const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

function setText(sel: string, text: string): void {
  const el = $(sel);
  if (el) el.textContent = text;
}

export function toast(msg: string): void {
  const t = document.createElement("div");
  t.textContent = msg;
  Object.assign(t.style, {
    position: "fixed", left: "50%", bottom: "24px", transform: "translateX(-50%)",
    background: "rgba(13,19,32,0.95)", color: "#fff", padding: "10px 16px",
    borderRadius: "12px", font: "600 13px/1.2 system-ui, sans-serif", zIndex: "9999",
    boxShadow: "0 12px 40px -8px rgba(0,0,0,0.6)", maxWidth: "80%", textAlign: "center",
  });
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2400);
}

// A short shutter click (Web Audio) so a capture has audible feedback. iOS allows this
// inside the shutter tap (a user gesture). Best-effort — silent if audio is blocked.
function shutterSound(): void {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.11);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
    setTimeout(() => void ctx.close(), 300);
  } catch {
    /* no audio */
  }
}

// ───────────────────────── state ─────────────────────────
let pages: Blob[] = []; // captured page images, in order
let thumbUrls: string[] = []; // object URLs to revoke on re-render (review grid)
let stripUrls: string[] = []; // object URLs for the capture-screen thumbnail strip
let stream: MediaStream | null = null; // active camera (capture or linkscan)
let scanAbort: AbortController | null = null; // in-app QR scan loop
let progTimer: number | null = null;
let linkFrom: ScanState = "pairing"; // where to return if linking is cancelled
let captureMode: ColorMode = "color"; // Colour / Grey / B&W toggle in the capture screen
let capturing = false; // guard against double-adds while a photo is being processed
let warnedNoCrop = false; // one warning per session when the detector itself is down
let scanDiags: string[] = []; // per-capture detect outcomes; travels invisibly in the PDF metadata
// Detected page corners per captured page, keyed by the page Blob so review-screen delete /
// retake / drag-reorder can never desync them (the WeakMap follows the Blob, not an index).
const cornersByPage = new WeakMap<Blob, NormQuad>();
let signOn = false; // "Sign" pill — stamp THIS scan with the user's synced signature (default off)
let signAvailable = false; // a signature has been synced from the desktop Doc Signer
const MODE_LABEL: Record<ColorMode, string> = { color: "Colour", grayscale: "Grey", bw: "B&W" };

const isActive = (s: ScanState): boolean =>
  $(`.ms-state[data-state="${s}"]`)?.classList.contains("active") ?? false;

// ───────────────────────── router ─────────────────────────
export function show(s: ScanState): void {
  stopCamera(); // releases capture/linkscan camera when leaving those states
  $$(".ms-state").forEach((el) => el.classList.toggle("active", el.getAttribute("data-state") === s));
  setNet(navigator.onLine && s !== "offline");

  if (s === "capture") void enterCapture();
  else if (s === "review") renderThumbs();
  else if (s === "sending") runSending();
  else if (s === "linkscan") void enterLink();
  else if (s === "error") setText("#errPages", `${pages.length} ${pages.length === 1 ? "page" : "pages"} kept`);
}

// ───────────────────────── screen router ─────────────────────────
// Top-level screens layered ABOVE the scan state-machine: Home launcher,
// the Scan tool (#scan — the existing flow), and Voice Memo. Only one is
// active at a time. (docscanner.css already owns `.screen`, so the wrappers
// use `.rp-screen`.) The scan `.ms-state` machine still runs inside #scan.
export type Screen = "home" | "scan" | "voicememo" | "contacts" | "faq" | "tasks" | "minutes" | "docs" | "quotes" | "receipts" | "voiceforms" | "connect";
const SCREEN_IDS: Record<Screen, string> = { home: "screen-home", scan: "scan", voicememo: "screen-voicememo", contacts: "screen-contacts", faq: "screen-faq", tasks: "screen-tasks", minutes: "screen-minutes", docs: "screen-docs", quotes: "screen-quotes", receipts: "screen-receipts", voiceforms: "screen-voiceforms", connect: "screen-connect" };
// The shared top-bar pill = the active tool's name (blank on Home). Canonical module
// names — keep in sync with the Outlook pane Tools list and the desktop PWA nav.
const SCREEN_PILL: Record<Screen, string> = { home: "", scan: "Scan", voicememo: "Voice Memo", contacts: "Contacts", faq: "FAQ", tasks: "Tasks", minutes: "Notes", docs: "Docs", quotes: "Quotes", receipts: "Receipts", voiceforms: "Voice Forms", connect: "Brain" };

export function showScreen(s: Screen): void {
  document.dispatchEvent(new CustomEvent("rp:screen", { detail: s }));
  for (const [key, id] of Object.entries(SCREEN_IDS)) {
    document.getElementById(id)?.classList.toggle("active", key === s);
  }
  document.getElementById("soonOv")?.classList.remove("open"); // never leave a soon overlay hanging
  // consistent top bar: pill shows the active tool (hidden on Home); the count chip is Contacts-only
  const pill = document.getElementById("topPill") as HTMLElement | null;
  const pillLabel = document.getElementById("topPillLabel");
  if (pill) pill.hidden = !SCREEN_PILL[s];
  if (pillLabel) pillLabel.textContent = SCREEN_PILL[s];
  const count = document.getElementById("topCount") as HTMLElement | null;
  const LIST_SCREENS: Screen[] = ["contacts", "faq", "tasks", "minutes", "docs", "quotes", "receipts", "voiceforms"];
  if (count && !LIST_SCREENS.includes(s)) count.hidden = true;
}

// Back to the launcher from any tool — release the camera if we were capturing.
// (No greeting on Home by design — Gery 2026-06-07, re-confirmed 2026-06-11:
// the top of Home is the search bar, nothing else.)
export function goHome(): void {
  stopCamera();
  showScreen("home");
}

// Green ring on the GP avatar = signed in (linked) AND online.
function setAcctOnline(on: boolean): void {
  document.getElementById("acctBtn")?.classList.toggle("online", on);
}

function setNet(online: boolean): void {
  const pill = $("#netPill");
  if (!pill) return;
  pill.classList.toggle("off", !online);
  // keep the leading icon, swap the trailing label text node
  const label = pill.lastChild;
  if (label && label.nodeType === Node.TEXT_NODE) label.textContent = online ? " Online" : " Offline";
}

// ───────────────────────── capture ─────────────────────────
function syncCaptureCounters(): void {
  setText("#capCount", String(Math.min(pages.length + 1, MAX_PAGES)));
  setText("#capDoneCount", String(pages.length));
  const send = $<HTMLElement>("#capSend"); // Send is live only once there's a page to send
  if (send) { send.style.opacity = pages.length ? "1" : "0.45"; send.style.pointerEvents = pages.length ? "auto" : "none"; }
}

const SIGN_PREF_KEY = "rplay-sign-sync";
const signPref = (): boolean => localStorage.getItem(SIGN_PREF_KEY) === "on";

// The "Sign" pill shows ONLY when the user enabled signing in Settings AND a signature is
// actually synced from the desktop Doc Signer — so it never appears in a broken/erroring state.
function paintSign(): void {
  const b = $<HTMLButtonElement>("#capSign");
  if (!b) return;
  if (!(signPref() && signAvailable)) { b.style.display = "none"; signOn = false; return; }
  b.style.display = "";
  b.style.background = signOn ? "#2DD1A7" : "rgba(0,0,0,.5)";
  b.style.color = signOn ? "#06251d" : "#fff";
  b.style.borderColor = signOn ? "#2DD1A7" : "rgba(255,255,255,.3)";
}

// Confirm with the backend whether a signature is synced (status only — no bytes).
async function refreshSign(): Promise<void> {
  try { signAvailable = (await getSignStatus()).synced; }
  catch { /* keep last-known */ }
  if (!signAvailable) signOn = false;
}

// Reflect the sign setting across the Settings sheet (segmented control + hint) and the pill.
function syncSignUI(): void {
  const pref = signPref();
  document.querySelectorAll<HTMLElement>("#sigSeg [data-sig-set]").forEach((b) =>
    b.classList.toggle("on", (b.getAttribute("data-sig-set") === "on") === pref));
  const hint = $("#sigHint");
  if (hint) hint.textContent = "Sign scans with your RPlay signature.";
  paintSign();
}

// Toggle the setting from the Settings sheet; turning it on confirms availability with the server.
async function setSignPref(on: boolean): Promise<void> {
  localStorage.setItem(SIGN_PREF_KEY, on ? "on" : "off");
  if (!on) signOn = false;
  syncSignUI();
  if (on) { await refreshSign(); syncSignUI(); }
}

async function enterCapture(): Promise<void> {
  syncCaptureCounters();
  setText("#capMode", MODE_LABEL[captureMode]);
  syncSignUI();                                        // reflect last-known state immediately
  if (signPref()) void refreshSign().then(syncSignUI); // confirm availability only when enabled
  renderStrip();
  const inp = $<HTMLInputElement>("#nativeCam");
  if (inp) inp.value = "";
  $("#capCap")?.classList.remove("cap-live");
  // Native-only capture: NO getUserMedia / live preview. The middle shutter (#shutterFallback) opens
  // the OS camera for a full-resolution, SHARP photo — the iOS video feed is capped ~720-1080p (blurry)
  // and can't be pushed without the drawImage-black bug. Each photo is auto-cropped on-device (Scanic).
  warmDetector(); // load the Scanic WASM for the on-device crop
}

// Re-warm every time the page comes back to the foreground: iOS suspends the page while the
// native camera sheet is up, killing an in-flight init — this restarts it BEFORE the photo
// lands in onNativeCapture (belt to detect.ts's getScannerRetry braces). Registered ONCE at
// module scope; warmDetector is a no-op when the scanner is already up.
document.addEventListener("visibilitychange", () => { if (!document.hidden) warmDetector(); });

function attachVideo(vf: Element, src: MediaStream): void {
  let v = vf.querySelector("video");
  if (!v) {
    v = document.createElement("video");
    v.setAttribute("playsinline", "");
    v.muted = true;
    Object.assign(v.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%",
      objectFit: "cover", borderRadius: "inherit", zIndex: "0",
    });
    vf.insertBefore(v, vf.firstChild);
  }
  v.srcObject = src;
  void v.play().catch(() => {});
}

function stopCamera(): void {
  scanAbort?.abort();
  scanAbort = null;
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
}

// NATIVE capture: a photo came back from the OS camera (the <label for=nativeCam>
// shutter or the viewfinder prompt). Used when iOS blocks the live camera.
function onNativeCapture(inp: HTMLInputElement): void {
  const f = inp.files?.[0];
  inp.value = ""; // reset so the next tap re-opens the camera even for the same shot
  if (!f || capturing) return;
  if (pages.length >= MAX_PAGES) { toast("That's the 10-page max"); return; }
  capturing = true;
  void (async () => {
    try {
      shutterSound();
      flashViewfinder();
      const full = await fileToCanvas(f);
      // The phone DETECTS but never cuts: the detected corners ride along as a hint and the
      // server crops (vision-model corners first) — an on-device miscut is unrecoverable
      // (2026-08-10 live rounds), a full frame always survives to be cropped later.
      const found = await detectPageCorners(full);
      // Detector down entirely? Say so once instead of failing silently (the silent no-crop
      // fallback hid the July CSP breakage for weeks).
      if (!found && detectorUnavailable() && !warnedNoCrop) {
        warnedNoCrop = true;
        toast("Page detection isn't running — the server will still crop your scan");
      }
      const blob = await finishCanvas(full, "color"); // keep colour; Send applies the chosen mode to ALL pages
      if (found) cornersByPage.set(blob, found.q);
      pages.push(blob);
      scanDiags.push(`p${pages.length}:${lastDetectDiag()}`);
      syncCaptureCounters();
      renderStrip();
    } catch {
      toast("Couldn't add that photo — try again");
    } finally {
      capturing = false;
    }
  })();
}

function flashViewfinder(): void {
  $("#capVf")?.animate([{ filter: "brightness(2.4)" }, { filter: "brightness(1)" }], { duration: 200, easing: "ease-out" });
}

// Thumbnail strip on the capture screen — captured pages build up visibly so the multi-page
// flow reads clearly (shown in live mode). URLs revoked each render to avoid leaks.
function renderStrip(): void {
  const strip = $("#capStrip");
  if (!strip) return;
  stripUrls.forEach((u) => URL.revokeObjectURL(u));
  stripUrls = [];
  strip.innerHTML = "";
  pages.forEach((blob, i) => {
    const url = URL.createObjectURL(blob);
    stripUrls.push(url);
    const t = document.createElement("div");
    t.className = "ms-strip-thumb";
    t.innerHTML = `<img src="${url}" alt="Page ${i + 1}" /><span>${i + 1}</span>`;
    strip.appendChild(t);
  });
  applyStripMode();
}

// Reflect the selected colour mode on the thumbnails via a cheap CSS filter (the real encoding
// happens at send) — so grey/bw shows immediately even though pages are kept in colour.
function applyStripMode(): void {
  const strip = $<HTMLElement>("#capStrip");
  if (!strip) return;
  strip.style.filter = captureMode === "grayscale" ? "grayscale(1)" : captureMode === "bw" ? "grayscale(1) contrast(2.2) brightness(1.05)" : "none";
}

// Fallback when getUserMedia is unavailable/denied: the OS photo/camera picker.
function fileFallback(): void {
  const inp = document.createElement("input");
  inp.type = "file";
  inp.accept = "image/*";
  inp.setAttribute("capture", "environment");
  inp.multiple = true;
  inp.onchange = () => {
    const files = Array.from(inp.files ?? []).slice(0, MAX_PAGES - pages.length);
    // TODO: downscale file-fallback images to ~1500px (capture.ts only downscales video frames).
    files.forEach((f) => pages.push(f));
    show(pages.length ? "review" : "ready");
  };
  inp.click();
}

// ───────────────────────── review ─────────────────────────
function renderThumbs(): void {
  const host = $("#thumbs");
  if (!host) return;
  thumbUrls.forEach((u) => URL.revokeObjectURL(u));
  thumbUrls = [];
  host.innerHTML = "";

  pages.forEach((blob, i) => {
    const url = URL.createObjectURL(blob);
    thumbUrls.push(url);
    const t = document.createElement("div");
    t.className = "ms-thumb";
    t.draggable = true;
    t.dataset.idx = String(i);
    t.innerHTML =
      `<img src="${url}" alt="Page ${i + 1}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit" />` +
      `<span class="pg">${i + 1}</span>` +
      `<span class="ops">` +
      `<button class="ret" title="Retake"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg></button>` +
      `<button class="del" title="Delete"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg></button>` +
      `</span>`;
    host.appendChild(t);
  });

  // add-page tile
  const add = document.createElement("div");
  add.className = "ms-thumb add" + (pages.length >= MAX_PAGES ? " locked" : "");
  add.innerHTML =
    `<span class="inner"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L8 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-4l-1.5-2z"/><circle cx="12" cy="13" r="3.5"/><path d="M12 11v4M10 13h4" stroke-width="1.6"/></svg>Add page</span>`;
  if (pages.length < MAX_PAGES) add.addEventListener("click", () => show("capture"));
  host.appendChild(add);

  setText("#rvCount", String(pages.length));
  setText("#sendCount", `(${pages.length} ${pages.length === 1 ? "page" : "pages"})`);
  $("#reviewEl")?.classList.toggle("full", pages.length >= MAX_PAGES);

  bindThumbOps(host);
  bindDnd(host);
}

function bindThumbOps(host: Element): void {
  $$(".ms-thumb .del", host).forEach((b) =>
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      const idx = Number(((e.currentTarget as HTMLElement).closest(".ms-thumb") as HTMLElement)?.dataset.idx);
      if (!Number.isNaN(idx)) pages.splice(idx, 1);
      if (pages.length === 0) return show("ready");
      renderThumbs();
    }),
  );
  $$(".ms-thumb .ret", host).forEach((b) =>
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      const idx = Number(((e.currentTarget as HTMLElement).closest(".ms-thumb") as HTMLElement)?.dataset.idx);
      // Retake = drop this page and capture again.
      if (!Number.isNaN(idx)) pages.splice(idx, 1);
      show("capture");
    }),
  );
}

let dragEl: HTMLElement | null = null;
function bindDnd(host: Element): void {
  $$<HTMLElement>(".ms-thumb:not(.add)", host).forEach((t) => {
    t.addEventListener("dragstart", () => { dragEl = t; t.classList.add("dragging"); });
    t.addEventListener("dragend", () => { t.classList.remove("dragging"); dragEl = null; commitOrder(host); });
    t.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (!dragEl || dragEl === t) return;
      const rect = t.getBoundingClientRect();
      const before = e.clientY - rect.top < rect.height / 2;
      host.insertBefore(dragEl, before ? t : t.nextSibling);
    });
  });
}

// Reorder pages[] to match the DOM order after a drag.
function commitOrder(host: Element): void {
  const order = $$<HTMLElement>(".ms-thumb:not(.add)", host).map((t) => Number(t.dataset.idx));
  pages = order.map((i) => pages[i]).filter(Boolean) as Blob[];
  renderThumbs();
}

// ───────────────────────── sending ─────────────────────────
function runSending(): void {
  // Fire the upload. The pipeline runs server-side as a background job and emails the
  // result, so 202 = accepted. The staged bar is a "sent it off" indicator; a failed
  // upload jumps to the error state (pages preserved).
  void (async () => {
    // Apply the chosen Colour/Grey/B&W to EVERY page (they're kept in colour) so the whole doc is one mode.
    const out = captureMode === "color" ? pages : await Promise.all(pages.map((b) => recolorBlob(b, captureMode)));
    // Corner hints in CURRENT page order (recolored copies are new Blobs, so read the hints
    // off the original page Blobs — indexes match 1:1).
    const hints = pages.map((p) => {
      const q = cornersByPage.get(p);
      return q ? [[q.tl.x, q.tl.y], [q.tr.x, q.tr.y], [q.br.x, q.br.y], [q.bl.x, q.bl.y]] : null;
    });
    const res = await processScan(out, signOn, scanDiags, hints);
    if (!res.ok && isActive("sending")) show("error");
  })();

  const fill = $<HTMLElement>("#progFill");
  const steps = ["#ps0", "#ps1", "#ps2", "#ps3"];
  steps.forEach((s) => $(s)?.classList.remove("on"));
  $("#ps0")?.classList.add("on");
  if (fill) fill.style.width = "0%";
  let pct = 0;
  if (progTimer) clearInterval(progTimer);
  progTimer = window.setInterval(() => {
    pct = Math.min(100, pct + 3);
    if (fill) fill.style.width = `${pct}%`;
    const idx = Math.min(3, Math.floor(pct / 26));
    steps.forEach((s, k) => $(s)?.classList.toggle("on", k <= idx));
    if (pct >= 100) {
      if (progTimer) clearInterval(progTimer);
      progTimer = null;
      window.setTimeout(() => { if (isActive("sending")) show("done"); }, 500);
    }
  }, 90);
}

// ───────────────────────── linking ─────────────────────────
async function enterLink(): Promise<void> {
  const vf = $("#linkVf");
  if (!vf) return;
  $("#codeBox")?.classList.remove("on");
  setText("#codeToggle", "Can't scan? Enter the code");
  try {
    stream = await openCamera();
    attachVideo(vf, stream);
    if (qrSupported()) {
      scanAbort = new AbortController();
      const v = vf.querySelector("video") as HTMLVideoElement | null;
      if (v) {
        scanQR(v, scanAbort.signal)
          .then((payload) => void completeLink({ token: tokenFromPayload(payload) }))
          .catch(() => {});
      }
    }
  } catch {
    toast("Camera unavailable — enter the code instead");
    $("#codeBox")?.classList.add("on");
  }
}

// Redeem the scanned token (or typed code) → server mints the session cookie.
async function completeLink(input: { token?: string; code?: string }): Promise<void> {
  const res = await redeemPairing(input);
  if (res.paired) {
    stopCamera();
    clearSnaps(); // a fresh link may be a different account — never show the old one's lists
    saveSnap("session", { email: res.email, initials: res.initials });
    setMask(res.email);
    setInitials(res.initials);
    setAcctOnline(true);
    // ‼️ Re-ask the Brain gate NOW. clearSnaps() above just wiped the cached connect-meta, and
    // the only other probe runs at app BOOT — so after a fresh link the Brain launch bar stayed
    // hidden until the user force-quit the app (found 2026-08-30, the day everyone relinked).
    // Dynamic import: connect.ts imports from this module, a static import would be a cycle.
    void import("./connect").then((m) => m.probeConnectTile());
    showScreen("home"); // land on the launcher after a fresh link
    toast("Phone linked to Outlook ✓");
  } else {
    toast("Couldn't link — open Tools → DocScanner in Outlook for a fresh code");
  }
}

// Fill the masked-email spans (shown in ready / sending / done) once we know it.
function setMask(email?: string): void {
  if (!email) return;
  $$<HTMLElement>(".ms-mask").forEach((el) => (el.textContent = email));
}

// Avatar (topbar + account sheet): the linked user's initials, derived server-side
// from the full email (the client only sees the masked form). Unpaired / unknown →
// the neutral person glyph, never another user's letters.
const AV_PERSON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
function setInitials(ini?: string): void {
  $$<HTMLElement>("#acctBtn, .rp-pf-av").forEach((el) => {
    if (ini) el.textContent = ini;
    else el.innerHTML = AV_PERSON;
  });
}

// Usage & Credits (account sheet) — read-only mirror of the desktop panel. ‼️ EVERY paired user
// sees their usage breakdown (Gery 2026-06-19), org included; org users just don't get the €5
// allowance bar / wallet (those are self-serve, and topping up lives in the Outlook/desktop app).
// Only hidden when the server can't read usage at all (so the phone never shows a misleading €0).
function renderUsage(u?: import("./session").UsageInfo | null): void {
  const group = document.getElementById("usageGroup");
  if (!group) return;
  if (!u) { group.hidden = true; return; }
  const fmt = (n: number) => `€${n.toFixed(2)}`;
  const setHidden = (id: string, hidden: boolean) => { const el = document.getElementById(id); if (el) (el as HTMLElement).hidden = hidden; };

  const period = document.getElementById("usagePeriod");
  if (period) {
    let resets = "";
    try { resets = u.resets_at ? new Date(u.resets_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""; } catch { resets = ""; }
    period.textContent = resets ? `This month · resets ${resets}` : "This month";
  }

  const selfServe = u.metering === "self_serve";
  const org = u.org;
  const orgHasLimit = !selfServe && !!org && org.limit_eur != null;
  // Bar + "left" line show for self-serve OR an org user WITH a per-user limit. Wallet = self-serve only.
  setHidden("usageBarWrap", !(selfServe || orgHasLimit));
  setHidden("usageLeft", !(selfServe || orgHasLimit));
  setHidden("usageWalletRow", !selfServe);
  setHidden("usageHint", !selfServe); // VAT / top-up note applies to the self-serve wallet only
  setHidden("usageOrgNote", !( !selfServe && !orgHasLimit )); // "managed by org" only when org has NO limit

  // Doc-storage meter (BACKLOG #009) — shown to everyone; the 10 GB cap is per user, not per plan.
  const st = u.storage;
  setHidden("usageStorageRow", !st);
  const stVal = document.getElementById("usageStorage");
  if (st && stVal) {
    const gb = st.used_bytes / 1073741824;
    const used = gb >= 0.1 ? `${gb.toFixed(1)} GB` : `${Math.max(1, Math.round(st.used_bytes / 1048576))} MB`;
    stVal.textContent = `${used} of ${Math.round(st.cap_bytes / 1073741824)} GB`;
  }

  // "This month" total — shown to EVERYONE. Self-serve & org-with-limit show "spent / limit".
  const spent = document.getElementById("usageSpent");
  if (spent) {
    if (selfServe) spent.textContent = `${fmt(u.spent_eur)} / ${fmt(u.allowance_eur)}`;
    else if (orgHasLimit) spent.textContent = `${fmt(org!.spent_retail_eur)} / ${fmt(org!.limit_eur!)}`;
    else spent.textContent = fmt(org ? org.spent_retail_eur : u.spent_eur); // org, no per-user limit
  }

  const fill = document.getElementById("usageFill");
  const left = document.getElementById("usageLeft");
  if (selfServe) {
    const wallet = document.getElementById("usageWallet");
    if (wallet) wallet.textContent = fmt(u.wallet_eur);
    if (left) left.textContent = `${fmt(Math.max(0, u.allowance_eur - u.spent_eur))} left of ${fmt(u.allowance_eur)} · ${Math.max(0, 100 - u.pct)}% left`;
    if (fill) {
      fill.style.width = `${Math.max(2, u.pct)}%`;
      fill.classList.toggle("warn", u.pct >= 80 && !u.over);
      fill.classList.toggle("over", u.over);
    }
  } else if (orgHasLimit) {
    const pct = org!.pct ?? 0;
    if (left) left.textContent = `${fmt(org!.remaining_eur ?? 0)} left of ${fmt(org!.limit_eur!)} · ${Math.max(0, 100 - pct)}% left · set by your organisation`;
    if (fill) {
      fill.style.width = `${Math.max(2, pct)}%`;
      fill.classList.toggle("warn", pct >= 80 && pct < 100);
      fill.classList.toggle("over", pct >= 100);
    }
  }

  // Breakdown — shown to EVERYONE: the 4 cost groups (with mini bars) + the emails-sent count.
  const bd = document.getElementById("usageBreakdown");
  if (bd) {
    const max = Math.max(0.01, ...u.groups.map((g) => g.eur));
    const rows = u.groups.map((g) => {
      const w = Math.max(g.eur > 0 ? 4 : 0, (g.eur / max) * 100);
      return `<div class="rp-usage-grp"><div class="rp-usage-grp-top"><span>${g.label}</span><span class="rp-usage-grp-val">${g.calls} · ${fmt(g.eur)}</span></div><div class="rp-usage-bar sm"><div class="rp-usage-fill g-${g.key}" style="width:${w}%"></div></div></div>`;
    }).join("");
    bd.innerHTML = rows + `<div class="rp-usage-grp"><div class="rp-usage-grp-top"><span>Emails sent</span><span class="rp-usage-grp-val">${u.emails_sent}</span></div></div>`;
  }
  group.hidden = false;
}

// Unlink drops the previous user's identity from the chrome so a re-link on a shared
// device can never show someone else's initials or a stale green ring.
export function resetIdentity(): void {
  setInitials(undefined);
  setAcctOnline(false);
  renderUsage(null);
}

// NOTE: org/subscription enrollment is intentionally absent on the companion (Gery 2026-06-23).
// You can NEVER choose how you use RPlay from the mobile companion — that flow (discovery /
// accept-invite / ask-to-join / personal plan) lives ONLY in the Outlook pane and desktop PWA.
// `renderUsage` above still shows org SPEND/limits for an already-enrolled org member; that is a
// read-only display, not a choice. Do not re-add an org banner / sheet / membership call here.

// ───────────────────────── actions ─────────────────────────
const ACTIONS: Record<string, () => void> = {
  toCapture: () => show("capture"),
  goHome: () => goHome(),
  cancelCapture: () => goHome(),
  toReview: () => show("review"),
  toSending: () => show("sending"),
  sendNow: () => { if (pages.length) show("sending"); }, // capture screen: email immediately, no review
  scanAnother: () => { resetPages(); show("capture"); },
  close: () => { resetPages(); goHome(); },
  retrySend: () => show("sending"),
  backReview: () => show("review"),
  retryNet: () => void boot(),
  toLink: () => { linkFrom = currentState() ?? "pairing"; show("linkscan"); },
  cancelLink: () => show(linkFrom),
  submitCode: () => {
    const code = $<HTMLInputElement>("#codeInput")?.value.trim() ?? "";
    if (code.replace(/[^a-z0-9]/gi, "").length >= 4) void completeLink({ code });
    else { toast("Enter the code from Outlook"); $("#codeInput")?.focus(); }
  },
  // org/subscription enrollment actions intentionally omitted on the companion — see note above.
};

function currentState(): ScanState | null {
  return ($(".ms-state.active")?.getAttribute("data-state") as ScanState | undefined) ?? null;
}

export function resetPages(): void {
  thumbUrls.forEach((u) => URL.revokeObjectURL(u));
  thumbUrls = [];
  stripUrls.forEach((u) => URL.revokeObjectURL(u));
  stripUrls = [];
  pages = [];
  scanDiags = [];
}

// ───────────────────────── boot / wiring ─────────────────────────
export async function boot(): Promise<void> {
  if (!navigator.onLine) { setAcctOnline(false); showScreen("scan"); return show("offline"); }
  // Optimistic restore: the last-known session paints Home on the first frame (no wait
  // on the probe round-trip); the probe below revalidates and corrects if it must.
  const cached = loadSnap<{ email?: string; initials?: string }>("session");
  if (cached) {
    setMask(cached.email);
    setInitials(cached.initials);
    setAcctOnline(true);
    showScreen("home");
  }
  const session = await probeSession();
  if (session.paired) {
    setRegion(session.number_format === "us" ? "us" : "eu"); // honour the account's EU/US format choice
    // Persist the account's chat-sound choice so connect.ts (read live from this snapshot) honours it.
    saveSnap("session", { email: session.email, initials: session.initials, audio_feedback: session.audio_feedback !== false });
    setMask(session.email);
    setInitials(session.initials);
    renderUsage(session.usage);
    setAcctOnline(true); // signed in + online → green ring
    if (!cached) showScreen("home"); // paired → the launcher (don't yank a user who already tapped a tile)
    return;
  }
  // The probe never reached the server (flaky network) while we hold a cached session →
  // keep the optimistic Home; the tiles surface their own errors. A real "not paired"
  // answer (revoked / cookie gone) falls through to the pairing screen.
  if (session.offline && cached) { setAcctOnline(false); return; }
  // No session → the scan shell handles linking. Route by install context.
  clearSnaps();
  setAcctOnline(false);
  showScreen("scan");
  show(displayContext() === "standalone" ? "pairing" : "browser");
}

export function initApp(): void {
  // delegated CTA actions
  document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!btn) return;
    ACTIONS[btn.getAttribute("data-act") ?? ""]?.();
  });
  // The OS camera returns its photo through the in-DOM file input's change event.
  $<HTMLInputElement>("#nativeCam")?.addEventListener("change", (e) => onNativeCapture(e.target as HTMLInputElement));

  $("#capMode")?.addEventListener("click", () => {
    captureMode = captureMode === "color" ? "grayscale" : captureMode === "grayscale" ? "bw" : "color";
    setText("#capMode", MODE_LABEL[captureMode]);
    applyStripMode(); // reflect the new mode on the captured-page thumbnails
  });

  // The pill only renders when usable (pref on + signature synced), so a tap simply arms/disarms it.
  $("#capSign")?.addEventListener("click", () => { signOn = !signOn; paintSign(); });

  // Settings → "Sync signature" Off/On (segmented, mirrors the theme switch).
  $("#sigSeg")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-sig-set]");
    if (b) void setSignPref(b.getAttribute("data-sig-set") === "on");
  });
  // Refresh the Settings hint (+ re-check sync status) whenever the account sheet opens.
  document.querySelectorAll<HTMLElement>('[data-act="openAccount"]').forEach((b) =>
    b.addEventListener("click", () => { syncSignUI(); if (signPref()) void refreshSign().then(syncSignUI); }));
  syncSignUI(); // initial paint of the Settings segmented control from the stored pref
  $("#a2hsX")?.addEventListener("click", () => { const a = $("#a2hs"); if (a) a.style.display = "none"; });

  const codeToggle = $("#codeToggle");
  codeToggle?.addEventListener("click", () => {
    const on = $("#codeBox")?.classList.toggle("on") ?? false;
    codeToggle.textContent = on ? "Hide code entry" : "Can't scan? Enter the code";
    if (on) $<HTMLInputElement>("#codeInput")?.focus();
  });

  window.addEventListener("offline", () => { setAcctOnline(false); showScreen("scan"); show("offline"); });
  window.addEventListener("online", () => void boot());

  if (location.search.includes("dev")) injectDevSwitcher();
  // Boot proof (c6): flips the build stamp to "c6 ✓" only once initApp has fully run and all
  // listeners are attached. If the user still sees plain "c6", the module never finished here.
  const bt = $("#buildtag");
  if (bt) bt.textContent = "c12 ✓";
}

// Dev-only: a floating switcher to preview every state (gated on ?dev).
function injectDevSwitcher(): void {
  const bar = document.createElement("div");
  Object.assign(bar.style, {
    position: "fixed", top: "6px", left: "6px", right: "6px", display: "flex",
    flexWrap: "wrap", gap: "4px", zIndex: "9998", justifyContent: "center",
  });
  const all: ScanState[] = ["ready", "pairing", "browser", "expired", "linkscan", "offline", "capture", "review", "sending", "done", "error"];
  all.forEach((s) => {
    const b = document.createElement("button");
    b.textContent = s;
    Object.assign(b.style, {
      font: "600 10px/1 system-ui", padding: "4px 6px", borderRadius: "6px",
      border: "1px solid rgba(127,127,127,.4)", background: "rgba(20,28,44,.85)", color: "#fff", cursor: "pointer",
    });
    b.addEventListener("click", () => show(s));
    bar.appendChild(b);
  });
  document.body.appendChild(bar);
}
